import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Mail, MessageCircle, Phone, MapPin, Linkedin } from 'lucide-react';
import { ReceevitLogoCompact } from '../common/ReceevitLogo';
import { useCookieConsent } from '../../contexts/CookieConsentContext';

const Footer = () => {
  const { openPreferences } = useCookieConsent();

  return (
    <footer className="relative bg-[#1F11ED] text-white overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-20 left-20 w-32 h-32 border border-white/10 rounded-full"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
            rotate: [360, 180, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-40 right-20 w-24 h-24 border border-white/10 rounded-full"
        />
        <motion.div
          animate={{
            x: [0, 80, 0],
            y: [0, -80, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 35,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-20 left-1/3 w-20 h-20 border border-white/10 rounded-full"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center mb-6">
              <ReceevitLogoCompact size={160} forBlueBackground={true} />
            </div>
            <p className="text-blue-100 mb-6 max-w-md">
              Streamline your invoicing process with our intelligent platform. 
              Get paid faster, reduce errors, and improve cash flow.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://www.linkedin.com/company/receevit/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center text-white hover:bg-white hover:text-[#1F11ED] transition-all duration-300"
                aria-label="Follow us on LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/features" className="text-blue-100 hover:text-white transition-colors duration-200">
                  Features
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-blue-100 hover:text-white transition-colors duration-200">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-blue-100 hover:text-white transition-colors duration-200">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/support" className="text-blue-100 hover:text-white transition-colors duration-200">
                  Support
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
            <ul className="space-y-3">
              <li className="flex items-center space-x-3 text-blue-100">
                <Mail className="w-4 h-4 text-white" />
                <span>hello@receevit.com</span>
              </li>
              <li className="flex items-center space-x-3 text-blue-100">
                <Phone className="w-4 h-4 text-white" />
                <span>+44 20 1234 5678</span>
              </li>
              <li className="flex items-center space-x-3 text-blue-100">
                <MapPin className="w-4 h-4 text-white" />
                <span>London, UK</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="mt-12 pt-8 border-t border-white/20">
          <div className="max-w-md">
            <h3 className="text-lg font-semibold text-white mb-3">Stay Updated</h3>
            <p className="text-blue-100 mb-4">Get the latest updates and insights delivered to your inbox.</p>
            <div className="flex space-x-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
              />
              <button className="px-6 py-2 bg-white text-[#1F11ED] rounded-lg font-semibold hover:bg-blue-50 transition-colors duration-200">
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/20 flex flex-col md:flex-row justify-between items-center">
          <p className="text-blue-100 text-sm">
            © {new Date().getFullYear()} Receevit®. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-4 md:mt-0">
            <Link to="/privacy" className="text-blue-100 hover:text-white text-sm transition-colors duration-200">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-blue-100 hover:text-white text-sm transition-colors duration-200">
              Terms of Use
            </Link>
            <Link to="/cookies" className="text-blue-100 hover:text-white text-sm transition-colors duration-200">
              Cookie Policy
            </Link>
            <button
              type="button"
              onClick={openPreferences}
              className="text-blue-100 hover:text-white text-sm transition-colors duration-200 underline-offset-4 hover:underline"
            >
              Cookie Preferences
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
